import React, { useState, useCallback } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Box,
  CircularProgress,
  Typography,
  IconButton,
  Checkbox,
} from '@mui/material'
import { Edit, Trash2, Star, Heart, Share2, Copy } from 'lucide-react'
import { useMobile } from '../../../hooks/useMobile'
import { MobileListCard } from '../MobileListCard'
import { PullToRefresh } from '../PullToRefresh'
import { BatchActionToolbar, type BatchAction } from '../BatchActionToolbar'
import { ContextMenu, type MenuItem } from '../ContextMenu'
import type { ResponsiveTableProps } from './types'

/**
 * Map icon name to component for context menu
 */
const iconMap: Record<string, React.ComponentType<{ size?: string | number }>> = {
  Edit,
  Trash2,
  Star,
  Heart,
  Share2,
  Copy,
}

export const ResponsiveTable = <T extends Record<string, any>>({
  columns,
  rows,
  loading,
  onRowClick,
  onEdit,
  onDelete,
  sortBy,
  sortOrder,
  onSort,
  emptyMessage = 'No data available',
  primaryField,
  getPrimaryText,
  secondaryField,
  getSecondaryText,
  statusField,
  getStatusColor,
  avatarField,
  // Pull-to-refresh props
  onRefresh,
  // Multi-select props
  selectable = false,
  selectedIds = [],
  onSelectionChange,
  onBatchDelete,
  // Context menu props
  contextMenuActions,
}: ResponsiveTableProps<T>) => {
  const { isMobile } = useMobile()

  // Selection mode state (internal state when selectable but no external control)
  const [internalSelectionMode, setInternalSelectionMode] = useState(false)
  const [internalSelectedIds, setInternalSelectedIds] = useState<string[]>([])

  // Context menu state
  const [contextMenuOpen, setContextMenuOpen] = useState(false)
  const [contextMenuRow, setContextMenuRow] = useState<T | null>(null)

  // Determine if we're in selection mode
  const isSelectionMode = selectable && (selectedIds.length > 0 || internalSelectionMode)
  const currentSelectedIds = selectedIds.length > 0 ? selectedIds : internalSelectedIds

  /**
   * Handle long press on a card (enters selection mode or shows context menu)
   */
  const handleLongPress = useCallback(
    (row: T) => {
      if (contextMenuActions && contextMenuActions.length > 0) {
        // Show context menu
        setContextMenuRow(row)
        setContextMenuOpen(true)
      } else if (selectable) {
        // Enter selection mode and select this item
        const id = row.id as string
        if (onSelectionChange) {
          onSelectionChange([id])
        } else {
          setInternalSelectionMode(true)
          setInternalSelectedIds([id])
        }
      }
    },
    [contextMenuActions, selectable, onSelectionChange]
  )

  /**
   * Handle selection change for a single item
   */
  const handleItemSelectionChange = useCallback(
    (row: T, selected: boolean) => {
      const id = row.id as string
      let newSelectedIds: string[]

      if (selected) {
        newSelectedIds = [...currentSelectedIds, id]
      } else {
        newSelectedIds = currentSelectedIds.filter((i) => i !== id)
      }

      if (onSelectionChange) {
        onSelectionChange(newSelectedIds)
      } else {
        setInternalSelectedIds(newSelectedIds)
      }
    },
    [currentSelectedIds, onSelectionChange]
  )

  /**
   * Handle select all
   */
  const handleSelectAll = useCallback(() => {
    const allIds = rows.map((row) => row.id as string)
    if (onSelectionChange) {
      onSelectionChange(allIds)
    } else {
      setInternalSelectedIds(allIds)
    }
  }, [rows, onSelectionChange])

  /**
   * Handle clear selection
   */
  const handleClearSelection = useCallback(() => {
    if (onSelectionChange) {
      onSelectionChange([])
    } else {
      setInternalSelectedIds([])
    }
  }, [onSelectionChange])

  /**
   * Handle cancel selection mode
   */
  const handleCancelSelection = useCallback(() => {
    handleClearSelection()
    setInternalSelectionMode(false)
  }, [handleClearSelection])

  /**
   * Handle batch delete
   */
  const handleBatchDelete = useCallback(() => {
    if (onBatchDelete) {
      onBatchDelete(currentSelectedIds)
    }
  }, [onBatchDelete, currentSelectedIds])

  /**
   * Close context menu
   */
  const handleCloseContextMenu = useCallback(() => {
    setContextMenuOpen(false)
    setContextMenuRow(null)
  }, [])

  // Loading state (same for both views)
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  // Empty state (same for both views)
  if (rows.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', p: 4 }}>
        <Typography color="text.secondary">{emptyMessage}</Typography>
      </Box>
    )
  }

  // Mobile: Card list
  if (isMobile) {
    /**
     * Render the card list
     */
    const renderCardList = () => (
      <Box sx={{ p: 2, pb: isSelectionMode ? 14 : 2 }}>
        {rows.map((row, index) => {
          // Determine primary field (title)
          const primaryCol = primaryField
            ? columns.find((col) => col.field === primaryField)
            : columns[0]

          if (!primaryCol) return null

          // Use getPrimaryText if provided, otherwise try to extract text
          let primaryText: string
          if (getPrimaryText) {
            primaryText = getPrimaryText(row)
          } else {
            const primaryValue = row[primaryCol.field]
            // If the value is an object (like a relation), try common name fields
            if (primaryValue && typeof primaryValue === 'object' && !Array.isArray(primaryValue)) {
              primaryText = primaryValue.name || primaryValue.title || primaryValue.label || String(primaryValue)
            } else {
              primaryText = String(primaryValue ?? '')
            }
          }

          // Determine secondary field (subtitle)
          let secondaryText: string | undefined
          if (getSecondaryText) {
            secondaryText = getSecondaryText(row)
          } else if (secondaryField) {
            const secondaryCol = columns.find((col) => col.field === secondaryField)
            if (secondaryCol) {
              const secondaryValue = row[secondaryCol.field]
              // If the value is an object, try common name fields
              if (secondaryValue && typeof secondaryValue === 'object' && !Array.isArray(secondaryValue)) {
                secondaryText = secondaryValue.name || secondaryValue.title || secondaryValue.label || String(secondaryValue)
              } else {
                secondaryText = String(secondaryValue ?? '')
              }
            }
          }

          // Determine status badge
          let status: { label: string; color: 'success' | 'warning' | 'error' | 'info' | 'default' } | undefined
          if (statusField) {
            const statusValue = row[statusField]
            if (statusValue) {
              status = {
                label: String(statusValue),
                color: getStatusColor ? getStatusColor(statusValue) : 'default',
              }
            }
          }

          // Determine avatar
          let avatar: string | undefined
          if (avatarField) {
            avatar = row[avatarField]
          }

          // Map remaining columns to fields (exclude primary, secondary, status, avatar)
          const excludedFields = new Set([
            primaryCol.field,
            secondaryField,
            statusField,
            avatarField,
          ].filter(Boolean))

          const fields = columns
            .filter((col) => !excludedFields.has(col.field))
            .map((col) => {
              const value = row[col.field]
              const displayValue = col.render ? col.render(value, row) : value

              return {
                label: col.headerName,
                value: displayValue ?? '-',
              }
            })

          const rowId = row.id as string
          const isSelected = currentSelectedIds.includes(rowId)

          return (
            <MobileListCard
              key={row.id || index}
              primaryText={primaryText}
              secondaryText={secondaryText}
              avatar={avatar}
              status={status}
              fields={fields}
              onEdit={onEdit ? () => onEdit(row) : undefined}
              onDelete={onDelete ? () => onDelete(row) : undefined}
              onTap={onRowClick && !isSelectionMode ? () => onRowClick(row) : undefined}
              onLongPress={
                (selectable || (contextMenuActions && contextMenuActions.length > 0))
                  ? () => handleLongPress(row)
                  : undefined
              }
              selectionMode={isSelectionMode}
              isSelected={isSelected}
              onSelectionChange={(selected) => handleItemSelectionChange(row, selected)}
            />
          )
        })}
      </Box>
    )

    /**
     * Build batch actions for toolbar
     */
    const batchActions: BatchAction[] = []
    if (onBatchDelete) {
      batchActions.push({
        id: 'delete',
        label: 'Delete',
        icon: Trash2,
        onClick: handleBatchDelete,
        variant: 'destructive',
        requiresConfirmation: true,
      })
    }

    /**
     * Build context menu items
     */
    const menuItems: MenuItem[] = contextMenuActions
      ? contextMenuActions.map((action) => ({
          id: action.id,
          label: action.label,
          icon: iconMap[action.icon] || Edit,
          onClick: () => {
            if (contextMenuRow) {
              action.onClick(contextMenuRow)
            }
            handleCloseContextMenu()
          },
        }))
      : []

    return (
      <>
        {/* Pull-to-refresh wrapper */}
        {onRefresh ? (
          <PullToRefresh
            onRefresh={onRefresh}
          >
            {renderCardList()}
          </PullToRefresh>
        ) : (
          renderCardList()
        )}

        {/* Batch action toolbar */}
        {isSelectionMode && (
          <BatchActionToolbar
            selectedCount={currentSelectedIds.length}
            totalCount={rows.length}
            selectedIds={currentSelectedIds}
            actions={batchActions}
            onCancel={handleCancelSelection}
            onSelectAll={handleSelectAll}
            onClearSelection={handleClearSelection}
          />
        )}

        {/* Context menu */}
        {contextMenuActions && contextMenuActions.length > 0 && (
          <ContextMenu
            open={contextMenuOpen}
            onClose={handleCloseContextMenu}
            items={menuItems}
          />
        )}
      </>
    )
  }

  // Desktop: Table
  const hasActions = onEdit || onDelete

  // Desktop selection handlers
  const allRowIds = rows.map((row) => row.id as string)
  const isAllSelected = selectable && allRowIds.length > 0 && allRowIds.every((id) => currentSelectedIds.includes(id))
  const isSomeSelected = selectable && currentSelectedIds.length > 0 && !isAllSelected

  const handleDesktopSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      handleSelectAll()
    } else {
      handleClearSelection()
    }
  }

  const handleDesktopRowSelect = (row: T, checked: boolean) => {
    handleItemSelectionChange(row, checked)
  }

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            {/* Checkbox column header */}
            {selectable && (
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={isSomeSelected}
                  checked={isAllSelected}
                  onChange={handleDesktopSelectAll}
                  inputProps={{ 'aria-label': 'select all' }}
                />
              </TableCell>
            )}
            {columns.map((column) => (
              <TableCell key={column.field}>
                {column.sortable && onSort ? (
                  <TableSortLabel
                    active={sortBy === column.field}
                    direction={sortBy === column.field ? sortOrder : 'asc'}
                    onClick={() => onSort(column.field)}
                  >
                    {column.headerName}
                  </TableSortLabel>
                ) : (
                  column.headerName
                )}
              </TableCell>
            ))}
            {hasActions && <TableCell>Actions</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => {
            const rowId = row.id as string
            const isRowSelected = selectable && currentSelectedIds.includes(rowId)

            return (
              <TableRow
                key={row.id || index}
                hover
                selected={isRowSelected}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                sx={{
                  cursor: onRowClick ? 'pointer' : 'default',
                  '&:hover': onRowClick
                    ? {
                        backgroundColor: 'action.hover',
                      }
                    : {},
                }}
              >
                {/* Checkbox column */}
                {selectable && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={isRowSelected}
                      onChange={(e) => {
                        e.stopPropagation()
                        handleDesktopRowSelect(row, e.target.checked)
                      }}
                      onClick={(e) => e.stopPropagation()}
                      inputProps={{ 'aria-label': `select row ${rowId}` }}
                    />
                  </TableCell>
                )}
                {columns.map((column) => {
                  const value = row[column.field]
                  const displayValue = column.render ? column.render(value, row) : value

                  return (
                    <TableCell key={column.field}>
                      {displayValue ?? '-'}
                    </TableCell>
                  )
                })}
                {hasActions && (
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {onEdit && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation()
                            onEdit(row)
                          }}
                          aria-label="Edit"
                        >
                          <Edit size={18} />
                        </IconButton>
                      )}
                      {onDelete && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation()
                            onDelete(row)
                          }}
                          aria-label="Delete"
                        >
                          <Trash2 size={18} />
                        </IconButton>
                      )}
                    </Box>
                  </TableCell>
                )}
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
